import { useCallback, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ThemeContext } from "../common/themeContext";
import { getAdminProfileHandler } from "../redux/action/getAdminProfileSlice";
import { MODULE_ACCESS_DENIED_MESSAGE } from "../common/apiResponse";
import {
  canAccessRoute,
  canAccessSidebarItem,
  canEditCampaigns,
  canViewCampaigns,
  getAdminRoleFromProfile,
  getDefaultRouteForPermissions,
  hasAnyPermission,
  hasPermission,
  isAdminRole,
  isProfilePermissionCheckPending,
  isSidebarMenuReady,
  isSuperAdmin,
  normalizePermissionList,
  PERMISSIONS,
  resolveAuthorizedRoute,
  shouldApplyPermissionFilter,
  shouldUseProductViewMode,
} from "../common/permissions";

export const DEFAULT_ADMIN_PERMISSION_GROUPS = [
  {
    title: "Analytics",
    permissions: [{ label: "ANALYTICS VIEW", checked: false }],
  },
  {
    title: "Campaigns",
    permissions: [
      { label: "CAMPAIGN VIEW", checked: false },
      { label: "CAMPAIGN EDIT", checked: false },
    ],
  },
  {
    title: "Users",
    permissions: [{ label: "USER VIEW", checked: false }],
  },
  {
    title: "Groups",
    permissions: [
      { label: "GROUP VIEW", checked: false },
      { label: "GROUP EDIT", checked: false },
    ],
  },
  {
    title: "Products",
    permissions: [
      { label: "PRODUCTS VIEW", checked: false },
      { label: "PRODUCTS EDIT", checked: false },
    ],
  },
  {
    title: "Email Templates",
    permissions: [
      { label: "EMAIL TEMPLATE VIEW", checked: false },
      { label: "EMAIL TEMPLATE EDIT", checked: false },
    ],
  },
  {
    title: "Notifications",
    permissions: [
      { label: "NOTIFICATION VIEW", checked: false },
      { label: "NOTIFICATION EDIT", checked: false },
    ],
  },
  {
    title: "Release Document",
    permissions: [
      { label: "RELEASE DOCUMENT VIEW", checked: false },
      { label: "RELEASE DOCUMENT EDIT", checked: false },
    ],
  },
  {
    title: "Admin",
    permissions: [
      { label: "ADMIN VIEW", checked: false },
      { label: "ADMIN EDIT", checked: false },
    ],
  },
];

const normalizeLabel = (label = "") =>
  String(label).trim().replace(/\s+/g, "_").toUpperCase();

const getProfileFromState = (profileState) => {
  const response = profileState?.data;
  return response?.data ?? response ?? null;
};

export const mergePermissionGroupsWithDefaults = (
  apiGroups = [],
  defaults = DEFAULT_ADMIN_PERMISSION_GROUPS,
) => {
  const checkedByLabel = new Map();

  (apiGroups || []).forEach((group) => {
    (group.permissions || []).forEach((permission) => {
      const label = permission?.label || permission?.value || permission;

      if (!label) {
        return;
      }

      const normalizedLabel = normalizeLabel(label);
      const checked = Boolean(permission?.checked);

      checkedByLabel.set(normalizedLabel, checked);

      if (normalizedLabel === "PRODUCT_VIEW" || normalizedLabel === "PRODUCTS_VIEW") {
        checkedByLabel.set("PRODUCT_VIEW", checked);
        checkedByLabel.set("PRODUCTS_VIEW", checked);
      }

      if (normalizedLabel === "PRODUCT_EDIT" || normalizedLabel === "PRODUCTS_EDIT") {
        checkedByLabel.set("PRODUCT_EDIT", checked);
        checkedByLabel.set("PRODUCTS_EDIT", checked);
      }
    });
  });

  return defaults.map((group) => ({
    title: group.title,
    permissions: group.permissions.map((permission) => {
      const normalizedLabel = normalizeLabel(permission.label);

      return {
        label: permission.label,
        checked: checkedByLabel.has(normalizedLabel)
          ? checkedByLabel.get(normalizedLabel)
          : permission.checked,
      };
    }),
  }));
};

export const getCheckedPermissionsFromGroups = (permissionGroups = []) =>
  normalizePermissionList(
    permissionGroups.flatMap((group) =>
      (group.permissions || [])
        .filter((permission) => permission?.checked)
        .map((permission) => normalizeLabel(permission.label)),
    ),
  );

export const getProductGroupPermissions = (permissionGroups = []) => {
  const productsGroup = permissionGroups.find(
    (group) => group.title === "Products",
  );

  const getChecked = (...labels) =>
    labels.some((label) =>
      Boolean(
        productsGroup?.permissions?.find((permission) => {
          const permissionLabel = normalizeLabel(
            permission?.label || permission?.value || "",
          );

          return (
            permission?.checked && permissionLabel === normalizeLabel(label)
          );
        })?.checked,
      ),
    );

  return {
    hasView: getChecked("PRODUCTS VIEW", "PRODUCT VIEW"),
    hasEdit: getChecked("PRODUCTS EDIT", "PRODUCT EDIT"),
  };
};

export const getGroupGroupPermissions = (permissionGroups = []) => {
  const groupsGroup = permissionGroups.find((group) => group.title === "Groups");

  const getChecked = (label) =>
    Boolean(
      groupsGroup?.permissions?.find(
        (permission) =>
          normalizeLabel(permission.label) === normalizeLabel(label),
      )?.checked,
    );

  return {
    hasView: getChecked("GROUP VIEW"),
    hasEdit: getChecked("GROUP EDIT"),
  };
};

export const getNotificationGroupPermissions = (permissionGroups = []) => {
  const notificationsGroup = permissionGroups.find(
    (group) => group.title === "Notifications",
  );

  const getChecked = (label) =>
    Boolean(
      notificationsGroup?.permissions?.find(
        (permission) =>
          normalizeLabel(permission.label) === normalizeLabel(label),
      )?.checked,
    );

  return {
    hasView: getChecked("NOTIFICATION VIEW"),
    hasEdit: getChecked("NOTIFICATION EDIT"),
  };
};

export const getReleaseDocumentGroupPermissions = (permissionGroups = []) => {
  const releaseDocumentGroup = permissionGroups.find(
    (group) => group.title === "Release Document",
  );

  const getChecked = (label) =>
    Boolean(
      releaseDocumentGroup?.permissions?.find(
        (permission) =>
          normalizeLabel(permission.label) === normalizeLabel(label),
      )?.checked,
    );

  return {
    hasView: getChecked("RELEASE DOCUMENT VIEW"),
    hasEdit: getChecked("RELEASE DOCUMENT EDIT"),
  };
};

export const getEmailTemplateGroupPermissions = (permissionGroups = []) => {
  const emailTemplatesGroup = permissionGroups.find(
    (group) => group.title === "Email Templates",
  );

  const getChecked = (label) =>
    Boolean(
      emailTemplatesGroup?.permissions?.find(
        (permission) =>
          normalizeLabel(permission.label) === normalizeLabel(label),
      )?.checked,
    );

  return {
    hasView: getChecked("EMAIL TEMPLATE VIEW"),
    hasEdit: getChecked("EMAIL TEMPLATE EDIT"),
  };
};

export const getCampaignGroupPermissions = (permissionGroups = []) => {
  const campaignsGroup = permissionGroups.find(
    (group) => group.title === "Campaigns",
  );

  const getChecked = (label) =>
    Boolean(
      campaignsGroup?.permissions?.find(
        (permission) =>
          normalizeLabel(permission.label) === normalizeLabel(label),
      )?.checked,
    );

  return {
    hasView: getChecked("CAMPAIGN VIEW"),
    hasEdit: getChecked("CAMPAIGN EDIT"),
  };
};

export const hasCampaignViewOnlyAccess = (permissionGroups = []) => {
  const { hasView, hasEdit } = getCampaignGroupPermissions(permissionGroups);
  return hasView && !hasEdit;
};

export const VIEW_ONLY_EMPTY_MESSAGE = "No data available yet";

export { MODULE_ACCESS_DENIED_MESSAGE };

export const resolveModuleAccessDeniedRoute = ({
  canView = false,
  canEdit = false,
  moduleRoute,
  defaultRoute = null,
}) => {
  if (canView || canEdit) {
    return moduleRoute ?? null;
  }

  return defaultRoute ?? null;
};

export const PRODUCT_PERMISSION_MESSAGES = {
  UPLOAD: VIEW_ONLY_EMPTY_MESSAGE,
  HTML: VIEW_ONLY_EMPTY_MESSAGE,
  CERTIFICATE: VIEW_ONLY_EMPTY_MESSAGE,
  CONTACT_NUMBER: VIEW_ONLY_EMPTY_MESSAGE,
};

export const getScheduleCareContactNumber = (content) => {
  const number = content?.number;

  if (number == null || number === "") {
    return "";
  }

  return String(number).trim();
};

export const hasScheduleCareContactNumber = (content) =>
  Boolean(getScheduleCareContactNumber(content));

export const hasProductCertificates = (content) => {
  if (!content) {
    return false;
  }

  const englishStates = content?.english?.coi?.state || [];
  const spanishStates = content?.spanish?.coi?.state || [];

  return [...englishStates, ...spanishStates].some(
    (item) => item?.url || item?.fileName || item?.preSigned,
  );
};

export const hasProductViewOnlyAccess = (permissionGroups = []) => {
  const { hasView, hasEdit } = getProductGroupPermissions(permissionGroups);
  return hasView && !hasEdit;
};

export const hasGroupViewOnlyAccess = (permissionGroups = []) => {
  const { hasView, hasEdit } = getGroupGroupPermissions(permissionGroups);
  return hasView && !hasEdit;
};

export const hasNotificationViewOnlyAccess = (permissionGroups = []) => {
  const { hasView, hasEdit } = getNotificationGroupPermissions(permissionGroups);
  return hasView && !hasEdit;
};

export const hasReleaseDocumentViewOnlyAccess = (permissionGroups = []) => {
  const { hasView, hasEdit } =
    getReleaseDocumentGroupPermissions(permissionGroups);
  return hasView && !hasEdit;
};

export const hasEmailTemplateViewOnlyAccess = (permissionGroups = []) => {
  const { hasView, hasEdit } =
    getEmailTemplateGroupPermissions(permissionGroups);
  return hasView && !hasEdit;
};

export const hasContentViewOnlyAccess = (
  permissionGroups = [],
  { isGroupContext = false, profileState = null } = {},
) => {
  if (!isAdminRole(profileState)) {
    return false;
  }

  return isGroupContext
    ? hasGroupViewOnlyAccess(permissionGroups)
    : hasProductViewOnlyAccess(permissionGroups);
};

export const canDeleteProductFiles = (permissionGroups = []) => {
  const { hasEdit } = getProductGroupPermissions(permissionGroups);
  return hasEdit;
};

export const canDeleteGroupFiles = (permissionGroups = []) => {
  const { hasEdit } = getGroupGroupPermissions(permissionGroups);
  return hasEdit;
};

export const canDeleteContentFiles = (
  permissionGroups = [],
  { isGroupContext = false } = {},
) =>
  isGroupContext
    ? canDeleteGroupFiles(permissionGroups)
    : canDeleteProductFiles(permissionGroups);

export const useAdminPermissions = ({
  autoFetch = false,
  force = false,
  refetchOnFocus = false,
} = {}) => {
  const dispatch = useDispatch();
  const { brandId } = useContext(ThemeContext);
  const profileState = useSelector((state) => state?.getAdminProfile);

  const profile = useMemo(
    () => getProfileFromState(profileState),
    [profileState],
  );

  const permissionGroups = useMemo(
    () => mergePermissionGroupsWithDefaults(profile?.permissions),
    [profile?.permissions],
  );

  const permissions = useMemo(
    () => getCheckedPermissionsFromGroups(permissionGroups),
    [permissionGroups],
  );

  const productPermissions = useMemo(
    () => getProductGroupPermissions(permissionGroups),
    [permissionGroups],
  );

  const canManageProductActions = useMemo(
    () => isSuperAdmin(profileState) || productPermissions.hasEdit,
    [productPermissions.hasEdit, profileState],
  );

  const hasProductViewOnly = useMemo(
    () =>
      isAdminRole(profileState) && hasProductViewOnlyAccess(permissionGroups),
    [permissionGroups, profileState],
  );

  const hasGroupViewOnly = useMemo(
    () => isAdminRole(profileState) && hasGroupViewOnlyAccess(permissionGroups),
    [permissionGroups, profileState],
  );

  const hasNotificationViewOnly = useMemo(
    () =>
      isAdminRole(profileState) &&
      hasNotificationViewOnlyAccess(permissionGroups),
    [permissionGroups, profileState],
  );

  const hasReleaseDocumentViewOnly = useMemo(
    () =>
      isAdminRole(profileState) &&
      hasReleaseDocumentViewOnlyAccess(permissionGroups),
    [permissionGroups, profileState],
  );

  const hasEmailTemplateViewOnly = useMemo(
    () =>
      isAdminRole(profileState) &&
      hasEmailTemplateViewOnlyAccess(permissionGroups),
    [permissionGroups, profileState],
  );

  const campaignPermissions = useMemo(
    () => getCampaignGroupPermissions(permissionGroups),
    [permissionGroups],
  );

  const hasCampaignViewOnly = useMemo(
    () => isAdminRole(profileState) && hasCampaignViewOnlyAccess(permissionGroups),
    [permissionGroups, profileState],
  );

  const role = getAdminRoleFromProfile(profileState);

  const fetchProfile = useCallback(
    (payload = {}, options = {}) =>
      dispatch(
        getAdminProfileHandler(
          {
            ...payload,
            ...(brandId != null ? { refBrandId: brandId } : {}),
          },
          options,
        ),
      ),
    [dispatch, brandId],
  );

  const refetchProfile = useCallback(
    (payload = {}) => fetchProfile(payload, { force: true }),
    [fetchProfile],
  );

  useEffect(() => {
    if (!autoFetch || !localStorage.getItem("token")) {
      return;
    }

    if (force || profileState?.data == null) {
      fetchProfile({}, { force });
    }
  }, [autoFetch, force, fetchProfile, profileState?.data, brandId]);

  useEffect(() => {
    if (!refetchOnFocus || !localStorage.getItem("token")) {
      return;
    }

    const handleWindowFocus = () => {
      fetchProfile({}, { force: true });
    };

    window.addEventListener("focus", handleWindowFocus);
    return () => window.removeEventListener("focus", handleWindowFocus);
  }, [fetchProfile, refetchOnFocus]);

  const checkPermission = useCallback(
    (permission) => hasPermission(permissions, permission),
    [permissions],
  );

  const checkAnyPermission = useCallback(
    (permissionList = []) => hasAnyPermission(permissions, permissionList),
    [permissions],
  );

  const checkRouteAccess = useCallback(
    (pathname) => canAccessRoute(pathname, permissions, profileState, brandId),
    [permissions, profileState, brandId],
  );

  const checkSidebarAccess = useCallback(
    (menuKey) =>
      canAccessSidebarItem(menuKey, permissions, profileState, brandId),
    [permissions, profileState, brandId],
  );

  const resolveRoute = useCallback(
    (pathname) =>
      resolveAuthorizedRoute(pathname, permissions, profileState, brandId),
    [permissions, profileState, brandId],
  );

  const showProductDeleteButton = useMemo(
    () => canDeleteProductFiles(permissionGroups),
    [permissionGroups],
  );

  return {
    profileState,
    profile,
    permissionGroups,
    permissions,
    productPermissions,
    hasProductViewOnly,
    hasGroupViewOnly,
    hasNotificationViewOnly,
    hasReleaseDocumentViewOnly,
    hasEmailTemplateViewOnly,
    hasCampaignViewOnly,
    campaignPermissions,
    showProductDeleteButton,
    role,
    isLoading: Boolean(profileState?.isLoading),
    error: profileState?.error ?? "",
    message: profileState?.message ?? "",
    isReady: shouldApplyPermissionFilter(profileState),
    isPending: isProfilePermissionCheckPending(profileState),
    isSidebarReady: isSidebarMenuReady(profileState),
    isSuperAdmin: isSuperAdmin(profileState),
    isAdmin: isAdminRole(profileState),
    useProductViewMode: shouldUseProductViewMode(profileState),
    canViewProducts:
      isSuperAdmin(profileState) ||
      productPermissions.hasView ||
      productPermissions.hasEdit,
    canEditProducts: canManageProductActions,
    canAddProducts: canManageProductActions,
    canManageProducts: canManageProductActions,
    canViewCampaigns: canViewCampaigns(permissions),
    canEditCampaigns: canEditCampaigns(permissions, profileState),
    defaultRoute: getDefaultRouteForPermissions(
      permissions,
      profileState,
      brandId,
    ),
    hasPermission: checkPermission,
    hasAnyPermission: checkAnyPermission,
    canAccessRoute: checkRouteAccess,
    canAccessSidebarItem: checkSidebarAccess,
    resolveAuthorizedRoute: resolveRoute,
    fetchProfile,
    refetchProfile,
    PERMISSIONS,
    DEFAULT_ADMIN_PERMISSION_GROUPS,
    PRODUCT_PERMISSION_MESSAGES,
  };
};
